import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api'

export interface User {
  id: string
  email: string
  name: string
  avatar?: string
  role: 'student' | 'instructor' | 'admin' | 'owner'
  account_type?: 'person' | 'organization'
  approval_status?: 'pending' | 'approved' | 'rejected'
  organization_id?: string | null
  subscription?: {
    tier: string
    status: string
    plan_name?: string
    plan_slug?: string
    expires_at?: string
    renewal_type?: string
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('token'))
  const isLoading = ref(false)

  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const isInstructor = computed(() => user.value?.role === 'instructor' || user.value?.role === 'admin')
  const isAdmin = computed(() => user.value?.role === 'admin')

  async function initialize() {
    await fetchUser()
  }

  async function fetchUser() {
    try {
      isLoading.value = true
      
      // Check local token first
      const localToken = localStorage.getItem('token')
      const userStr = localStorage.getItem('user')
      
      if (localToken && userStr) {
        token.value = localToken
        user.value = JSON.parse(userStr)
        return
      }
      
      // Fallback: try to get session from LMS backend
      const response = await api.get('/auth/get-session')
      if (response.data.user) {
        user.value = response.data.user
        token.value = response.data.token || localStorage.getItem('token')
      } else {
        user.value = null
        token.value = null
      }
    } catch (err) {
      user.value = null
      token.value = null
    } finally {
      isLoading.value = false
    }
  }

  function setAuth(userData: User, authToken: string) {
    user.value = userData
    token.value = authToken
    localStorage.setItem('token', authToken)
    localStorage.setItem('user', JSON.stringify(userData))
  }

  function clearAuth() {
    user.value = null
    token.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }

  async function logout(returnUrl?: string) {
    // Call LMS backend logout
    try {
      await api.post('/auth/logout')
    } catch (error) {
      console.warn('Logout API call failed:', error)
    }
    
    // Clear local state
    clearAuth()
    
    // Broadcast logout to other tabs
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('auth')
      channel.postMessage({ type: 'logout' })
    }
    
    // Redirect to local login
    const redirectUrl = returnUrl || '/login'
    window.location.href = redirectUrl
  }

  return {
    user,
    token,
    isLoading,
    isAuthenticated,
    isInstructor,
    isAdmin,
    initialize,
    fetchUser,
    setAuth,
    clearAuth,
    logout,
  }
})
